export default `# Persisting Data in Local Storage

You might have noticed that after visiting a specific movie page when you go back to the movie list, all our settings: \`genre\`, \`year\`, \`rating\` and \`runtime\`, are forgotten.

It happens because the \`Navigation\` component state is always initialised with the same state.
And the selected \`genre\` and sliders positions are set based on these initial values.

## Saving Data in Local Storage

To fix this problem, we could use a more advanced solution, like Redux – a state management library. But because this course is about React, we will go
for a simpler solution.

We will store our choices in the *Local Storage*.

The *Local Storage* is an HTML5 feature that allows you to store the data in your browser. The *Local Storage* stores data in the form of JSON strings.

When you open Developers Tools and go to the *Application* tab, on the left-hand side you can inspect the *Local Storage*, *Session Storage*, *Cookies* and many more.

This is where web applications store data, information about website users and the state of the application.

For example, this course stores data about chapters in the *Local Storage*:

![local storage](https://sweetpumpkins.codecamps.com/localStorage.png)

Let's create two methods – \`saveStateToLocalStorage\` and \`getStateFromLocalStorage\` that will save and restore the state of the application to and from the *Local Storage*:

\`\`\`plainJsx
// Main.js

saveStateToLocalStorage = () => {
  localStorage.setItem("sweetpumpkins.params", JSON.stringify(this.state));
}

getStateFromLocalStorage = () => {
  return JSON.parse(localStorage.getItem("sweetpumpkins.params"));
}
\`\`\`

Before we save the \`state\` object to the *Local Storage* we have to translate it into a string. That's why the \`saveStateToLocalStorage\` method
uses the \`JSON.stringify\` before it stores the \`state\` in the *Local Storage*.

The \`getStateFromLocalStorage\` method returns data from the *Local Storage*.

The \`data\` in the *Local Storage* is stored as a string. And we have to parse this data back to a JavaScript object using the \`JSON.parse\` method.

We want to persist data from the \`state\` in the *Local Storage* every time when the \`state\` changes.

Let's add the \`saveStateToLocalStorage\` method to the \`componentWillUpdate\` method:

\`\`\`plainJsx
// Main.js

componentWillUpdate(nextProps, nextState) {
  this.saveStateToLocalStorage();
  if (this.state.moviesUrl !== nextState.moviesUrl) {
    this.fetchMovies(nextState.moviesUrl);
  }
  if (this.state.page !== nextState.page) {
    this.generateUrl();
  }
}
\`\`\`

Whenever we visit the \`Main\` page, our application should check if the \`state\` was previously saved in the *Local Storage*.
The best place to place this logic is the \`componentDidMount\` method. It's always called after a component gets rendered:

\`\`\`plainJsx
// Main.js

componentDidMount(){
  const savedState = this.getStateFromLocalStorage();
  if ( !savedState || (savedState && !savedState.movies.length)) {
    this.fetchMovies(this.state.moviesUrl);
  } else {
    this.setState({ ...savedState });
    this.generateUrl(savedState);
  }
}
\`\`\`

Above, we have retrieved the data from the *Local Storage* using the \`getStateFromLocalStorage\`.
If the \`state\` has not been persisted in the *Local Storage* – or it has been, but there are no movies in the \`savedState.movies\` array – we should fetch movies.

Otherwise, we should save the retrieved data in the \`state\` and finally generate the new url, based on the retrieved data.

Until now, the \`generateUrl\` method, has only been using data from the \`state\`.

Now, we want it to accept the parameter. The reason for that is that the \`setState\` method is asynchronous. And it may not always update the \`state\`
immediately.

Change the \`generateUrl\` method so that it accepts params and replace, in the first two lines, \`this.state\` with \`params\`:

\`\`\`plainJsx
// Main.js

generateUrl = params => {
  const {genres, year, rating, runtime, page } = params;
  const selectedGenre = genres.find( genre => genre.name === params.genre);
  const genreId = selectedGenre.id;

  const moviesUrl = \`https://api.themoviedb.org/3/discover/movie?\` +
    \`api_key=651925d45022d1ae658063b443c99784&\` +
    \`language=en-US&sort_by=popularity.desc&\` +
    \`with_genres=\${genreId}&\` +
    \`primary_release_date.gte=\${year.value.min}-01-01&\` +
    \`primary_release_date.lte=\${year.value.max}-12-31&\` +
    \`vote_average.gte=\${rating.value.min}&\` +
    \`vote_average.lte=\${rating.value.max}&\` +
    \`with_runtime.gte=\${runtime.value.min}&\` +
    \`with_runtime.lte=\${runtime.value.max}&\` +
    \`page=\${page}\`;

  this.setState({ moviesUrl });
}
\`\`\`

The \`generateUrl\` method is called in two other places in the \`Main\` component. Change the calls by passing the parameter:

\`\`\`plainJsx
// Main.js

onSearchButtonClick = () => {
  this.setState({page: 1});
  this.generateUrl(this.state);
}
\`\`\`

And:

\`\`\`plainJsx
// Main.js

componentWillUpdate(nextProps, nextState) {
  this.saveStateToLocalStorage();
  if (this.state.moviesUrl !== nextState.moviesUrl) {
    this.fetchMovies(nextState.moviesUrl);
  }
  if (this.state.page !== nextState.page) {
    this.generateUrl(nextState);
  }
}
\`\`\`

Now, the *Sweet Pumpkins* will remember what you were up to when you were browsing the movies last time!

Here is our final code:
\`\`\`codeSandbox
qql2jl0zj6
\`\`\`

## Summary

You completed the course! Congratulations! I hope you have enjoyed it and learned how to build a web application with React.

If you want to learn more about React, Redux or GraphQL in a week, book your place at one of our [upcoming bootcamps](http://www.codecamps.com/#new-courses).

Happy coding,

[Maciej](https://twitter.com/nowakowskipl)
`;



// WEBPACK FOOTER //
// ./src/book/chapters/local-storage.js